/**
 * Voice catalogue — session-scoped cache for the GET /voices payload.
 *
 * Shape of the cached object (matches the live `VoiceCatalogueResponse`):
 *   {
 *     tts_model: string,
 *     voices: Array<{ id: string, name: string, language: string, gender: string }>,
 *     system_default_voice: string,   // voice id (always present in `voices`)
 *   }
 *
 * Backend compatibility note: the public API moved from `voices: string[]`
 * (bare IDs) to `voices: { id, name, language, gender }[]`. `normalizeVoice`
 * accepts either shape so old responses and 422 echoes continue to work; in
 * both cases the catalogue is stored as objects.
 *
 * Lifecycle (per spec 010 data-model.md):
 *   get() — returns the cached catalogue if present; otherwise dedups on the
 *     in-flight fetch promise; otherwise issues a single `GET /voices` and
 *     stores the response.
 *   refreshFromErrorBody(body) — partial refresh from a 422 voice-validation
 *     echo. Replaces `cached.voices` and preserves cached `tts_model`
 *     (server-resolved, from GET /voices) and `system_default_voice`. If the cache is empty when a 422 lands, falls
 *     back to a full `get()`.
 *   clear() — wipes cache + in-flight slot and notifies subscribers; wired
 *     into the logout flow via `registerSignOutCleanup`.
 *   subscribe(fn) — pickers register a notify callback so they can re-render
 *     after `refreshFromErrorBody` or `clear`. Returns an unsubscribe fn.
 */

import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken, registerSignOutCleanup } from './auth';

let cached = null;
let inflight = null;
const subscribers = new Set();

function notify() {
  for (const fn of subscribers) {
    try { fn(); } catch (e) { console.warn('voiceCatalogue: subscriber threw', e); }
  }
}

function normalizeVoice(v) {
  if (typeof v === 'string' && v.length > 0) {
    return { id: v, name: v, language: '', gender: '' };
  }
  if (v && typeof v === 'object' && typeof v.id === 'string' && v.id.length > 0) {
    return {
      id: v.id,
      name: typeof v.name === 'string' && v.name.length > 0 ? v.name : v.id,
      language: typeof v.language === 'string' ? v.language : '',
      gender: typeof v.gender === 'string' ? v.gender : '',
    };
  }
  return null;
}

function normalizeVoices(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const v of list) {
    const norm = normalizeVoice(v);
    if (norm) out.push(norm);
  }
  return out;
}

function normalize(data) {
  const safe = (data && typeof data === 'object') ? data : {};
  return {
    tts_model: typeof safe.tts_model === 'string' ? safe.tts_model : '',
    voices: normalizeVoices(safe.voices),
    system_default_voice: typeof safe.system_default_voice === 'string'
      ? safe.system_default_voice
      : '',
  };
}

export function get() {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const token = getAccessToken();
      const headers = token
        ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { 'Content-Type': 'application/json' };
      const response = await axios.get(getApiUrl('voices'), { headers });
      cached = normalize(response && response.data);
      notify();
      return cached;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function refreshFromErrorBody(body) {
  const raw = body && Array.isArray(body.voice_catalogue) ? body.voice_catalogue : null;
  const echoed = raw ? normalizeVoices(raw) : null;
  if (!echoed) {
    if (cached) return Promise.resolve(cached);
    return get();
  }
  if (cached) {
    cached = { ...cached, voices: echoed };
    notify();
    return Promise.resolve(cached);
  }
  return get();
}

export function getCached() {
  return cached;
}

export function clear() {
  cached = null;
  inflight = null;
  notify();
}

export function subscribe(fn) {
  if (typeof fn !== 'function') return () => {};
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
}

export function _resetForTest() {
  cached = null;
  inflight = null;
  subscribers.clear();
}

registerSignOutCleanup(clear);
